import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';

interface BrandConfig {
  app_name?: string;
  primary_color: string;
  accent_color: string;
  success_color: string;
  /** Dark canvas base. Internal system-layer input (default RoofVox Brand Navy): no tenant UI edits it. */
  base_color?: string;
  logo_light_url: string;
  logo_dark_url: string;
  favicon_url: string;
}

/** Brand keys a tenant can clear back to the platform default. */
export type UnsettableBrandKey = 'primary_color' | 'accent_color' | 'success_color' | 'base_color';

interface ApiResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T;
}

interface UploadResponse {
  url: string;
}

export const brandApi = createApi({
  reducerPath: 'brandApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Brand'],
  endpoints: (builder) => ({
    getBrandConfig: builder.query<BrandConfig, void>({
      query: () => '/api/v1/configurations/brand',
      transformResponse: (res: ApiResponse<{ effective_value: BrandConfig }>) =>
        res.data?.effective_value ?? ({} as BrandConfig),
      providesTags: ['Brand'],
    }),

    // `unset` clears tenant-layer overrides so those keys fall back to the platform default. Use it
    // instead of writing the default value back, which would pin a copy that outlives later default changes.
    updateBrandConfig: builder.mutation<void, Partial<BrandConfig> & { unset?: UnsettableBrandKey[] }>({
      query: ({ unset, ...value }) => ({
        url: '/api/v1/configurations/brand',
        method: 'PUT',
        body: unset?.length ? { value, unset } : { value },
      }),
      // Refetch after an unset: the platform default that takes over is not known client-side.
      invalidatesTags: (_result, _error, arg) => (arg.unset?.length ? ['Brand'] : []),
      // Optimistically patch the cache so ThemeLoader re-applies the new color immediately
      // instead of re-fetching stale data after invalidation (which caused the theme to revert
      // until all wizard steps were done and the fetch completed).
      async onQueryStarted({ unset, ...patch }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          brandApi.util.updateQueryData('getBrandConfig', undefined, (draft) => {
            Object.assign(draft, patch);
            // Clear unset keys so stale overrides don't persist in the cache while the refetch lands.
            unset?.forEach((key) => { delete (draft as Record<string, unknown>)[key]; });
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    uploadLogo: builder.mutation<string, FormData>({
      query: (formData) => ({
        url: '/api/v1/uploads/logo',
        method: 'POST',
        body: formData,
      }),
      transformResponse: (res: ApiResponse<UploadResponse>) => res.data.url,
    }),
  }),
});

export const { useGetBrandConfigQuery, useUpdateBrandConfigMutation, useUploadLogoMutation } =
  brandApi;
